import React, { useState, useEffect } from 'react';
import { useListasConfig } from './useListasConfig';
import { db } from '../firebase';
import {
  collection, getDocs, addDoc, updateDoc, doc,
  orderBy, query, serverTimestamp, getDoc
} from 'firebase/firestore';
import { useColeccion, useParchear, BarraActualizacion, useEstadoPersistente, useAlCambiar, usePosicionScroll, useBorrador, useDescartarBorrador, useHayBorrador, useBorradorPendiente, AvisoBorrador } from './DatosContext';
import { BotonSolicitarEliminacion, AvisoSolicitudEliminacion } from './SolicitudEliminacion';
import DialogoConfirmar from './Dialogo';
import FichaOportunidad, { OPCIONES_ESTADO_MANUAL } from './FichaOportunidad';
import { ejecutarEliminacion } from './operaciones';
import useTecladoLista, { ESTILO_ACTIVO } from './useTecladoLista';

// ── CONSTANTES ───────────────────────────────────────────────────────────────


const ESTADOS_POSIBLE = [
  { valor: 'pendiente', etiqueta: 'Pendiente', color: '#534AB7' },
  { valor: 'presupuestado', etiqueta: 'Presupuestado', color: '#BA7517' },
  { valor: 'aceptado', etiqueta: 'Aceptado', color: '#1D9E75' },
  { valor: 'rechazado', etiqueta: 'Rechazado', color: '#D85A30' },
  { valor: 'descartado', etiqueta: 'Descartado', color: '#6b7280' },
];

function estadoPosible(valor) {
  return ESTADOS_POSIBLE.find(e => e.valor === valor) || ESTADOS_POSIBLE[0];
}

function EstadoBadge({ estado }) {
  const found = estadoPosible(estado);
  return (
    <span style={{
      background: found.color, color: '#fff',
      fontSize: '0.72rem', fontWeight: '500',
      padding: '3px 10px', borderRadius: '20px', whiteSpace: 'nowrap',
    }}>
      {found.etiqueta}
    </span>
  );
}

function formatearFechaCorta(fecha) {
  if (!fecha) return '—';
  const d = fecha.toDate ? fecha.toDate() : new Date(fecha);
  const meses = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${d.getDate()} ${meses[d.getMonth()]} ${d.getFullYear()}`;
}

function formatearFechaInput(fecha) {
  if (!fecha) return '';
  const d = fecha.toDate ? fecha.toDate() : new Date(fecha);
  return d.toISOString().split('T')[0];
}

function formatearEuros(valor) {
  if (!valor) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(valor);
}
const fmtNum = (n) => n ? new Intl.NumberFormat('es-ES').format(n) : '—';

function KpiConversion({ label, num, den, descripcion }) {
  const pct = den > 0 ? Math.round((num / den) * 100) : null;
  return (
    <div style={styles.kpiCard}>
      <p style={styles.kpiLabel}>{label}</p>
      <p style={{ ...styles.kpiValor, color: '#7B6FE8' }}>
        {pct !== null ? `${pct}%` : '—'}
        {pct !== null && (
          <span style={{ fontSize: '0.9rem', fontWeight: '400', color: '#AFA9EC', marginLeft: '8px' }}>
            ({num} de {den})
          </span>
        )}
      </p>
      {descripcion && <p style={{ color: '#6b7280', fontSize: '0.70rem', marginTop: '6px', lineHeight: '1.3' }}>{descripcion}</p>}
    </div>
  );
}

// ── MODAL CONFIRMAR SALIDA ────────────────────────────────────────────────────

function ModalConfirmar({ titulo, mensaje, onConfirmar, onCancelar, textoCancelar, textoConfirmar }) {
  // Sesión 34: todos los avisos usan el diálogo común (la opción segura es la destacada; Enter/Esc la eligen)
  const esBorrado = /eliminar|eliminando/i.test(textoConfirmar || '');
  return (
    <DialogoConfirmar
      titulo={titulo || 'Cambios sin guardar'}
      mensaje={mensaje}
      textoSeguro={textoCancelar && textoCancelar !== 'Cancelar' ? textoCancelar : (esBorrado ? 'No eliminar' : 'Seguir editando')}
      textoPeligroso={textoConfirmar || 'Salir sin guardar'}
      onSeguro={onCancelar}
      onPeligroso={onConfirmar}
      ocupado={/^eliminando/i.test(textoConfirmar || '')}
    />
  );
}

function estiloInput(error) {
  return {
    background: '#0e0b24',
    border: `1px solid ${error ? '#D85A30' : '#534AB7'}`,
    borderRadius: '8px', padding: '10px 14px',
    color: '#ffffff', fontSize: '0.9rem', width: '100%',
    outline: 'none', fontFamily: 'DM Sans, sans-serif', colorScheme: 'dark',
  };
}

function Campo({ label, children, error, fullWidth }) {
  return (
    <div style={{ gridColumn: fullWidth ? '1 / -1' : 'span 1', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <label style={styles.label}>{label}</label>
      {children}
      {error && <span style={{ color: '#D85A30', fontSize: '0.78rem' }}>{error}</span>}
    </div>
  );
}

function DatoFila({ label, valor, accion }) {
  return (
    <div style={styles.datoFila}>
      <span style={styles.datoLabel}>{label}</span>
      {accion ? (
        <a href={accion} style={{ ...styles.datoValor, color: '#7B6FE8', textDecoration: 'underline' }}>{valor}</a>
      ) : (
        <span style={styles.datoValor}>{valor}</span>
      )}
    </div>
  );
}

// ── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────

export default function Leads({ esAdmin, accionInicial, onAccionCompletada, onNavegar }) {
  const [vista, setVista] = useState('listado');
  const [leadSeleccionado, setLeadSeleccionado] = useState(null);
  // Sesión 32 · Entrega B: los leads viven en el almacén compartido (se descargan una vez por sesión).
  // Este módulo ya no descarga las oportunidades (nunca las mostraba): solo parchea la copia compartida
  // cuando las crea, edita o borra, para que Oportunidades las vea al día.
  const { items: leads, setItems: setLeads, cargando } = useColeccion('leads');
  const setTodasOportunidades = useParchear('oportunidades');

  // Acción entrante desde Dashboard
  useEffect(() => {
    if (!accionInicial) return;
    if (accionInicial.accion === 'nuevo') {
      setVista('alta');
      if (onAccionCompletada) onAccionCompletada();
    } else if (accionInicial.accion === 'ficha' && accionInicial.id) {
      // Esperamos a que carguen los leads y luego abrimos la ficha
      const abrir = async () => {
        const snap = await import('firebase/firestore').then(({ doc, getDoc }) => getDoc(doc(db, 'leads', accionInicial.id)));
        if (snap.exists()) { setLeadSeleccionado({ id: snap.id, ...snap.data() }); setVista('ficha'); }
        if (onAccionCompletada) onAccionCompletada();
      };
      abrir();
    }
  }, [accionInicial]);

  const abrirFicha = (lead) => { setLeadSeleccionado(lead); setVista('ficha'); };
  const actualizarLeadEnMemoria = async (leadId) => {
    try {
      const snap = await getDoc(doc(db, 'leads', leadId));
      if (snap.exists()) {
        const leadActualizado = { id: snap.id, ...snap.data() };
        setLeads(prev => prev.map(l => l.id === leadId ? leadActualizado : l));
        setLeadSeleccionado(leadActualizado);
      }
    } catch (e) { console.error(e); }
  };

  const volver = () => { setVista('listado'); setLeadSeleccionado(null); };

  // Sesión 34: si se salió del módulo con un borrador sin guardar en una ficha, se vuelve a ella
  const borradorPendiente = useBorradorPendiente('lead');
  const borradorAbierto = React.useRef(false);
  useEffect(() => {
    if (borradorAbierto.current || !borradorPendiente || cargando || accionInicial) return;
    borradorAbierto.current = true;
    if (borradorPendiente === 'nuevo') { setVista('alta'); return; }
    const l = leads.find(x => x.id === borradorPendiente);
    if (l) { setLeadSeleccionado(l); setVista('ficha'); }
  }, [borradorPendiente, cargando, leads, accionInicial]);

  const onGuardarNuevoLead = (nuevoLead) => {
    setLeads(prev => [nuevoLead, ...prev]);
    volver();
  };

  const onGuardarNuevaOportunidad = (nuevaOportunidad) => {
    setTodasOportunidades(prev => [nuevaOportunidad, ...prev]);
    volver();
  };

  if (vista === 'alta') return <FormularioAltaLead onGuardar={onGuardarNuevoLead} onCancelar={() => setVista('listado')} />;
  if (vista === 'nueva_oportunidad') return <FormularioNuevaOportunidadDirecta leads={leads} onGuardar={onGuardarNuevaOportunidad} onCancelar={() => setVista('listado')} />;
  if (vista === 'ficha' && leadSeleccionado) return <FichaLead
    lead={leadSeleccionado}
    onVolver={volver}
    esAdmin={esAdmin}
    onActualizar={() => actualizarLeadEnMemoria(leadSeleccionado.id)}
    onNuevaOportunidad={(op) => setTodasOportunidades(prev => [op, ...prev])}
    onNavegar={onNavegar}
    onEliminarLead={(id) => { setLeads(prev => prev.filter(l => l.id !== id)); setTodasOportunidades(prev => prev.filter(o => o.leadId !== id)); volver(); }}
  />;

    return (
    <div style={styles.container}>
      <div style={styles.cabecera}>
        <h1 style={styles.titulo}>Leads</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <BarraActualizacion colecciones={['leads']} />
          <button style={styles.botonPrimario} onClick={() => setVista('alta')}>+ Nuevo lead</button>
        </div>
      </div>

      <div style={{ padding: '24px 32px 32px' }}>
        {cargando ? (
          <p style={{ color: '#AFA9EC', padding: '32px 0' }}>Cargando...</p>
        ) : (
          <ListadoLeads leads={leads} onAbrirFicha={abrirFicha} />
        )}
      </div>
    </div>
  );
}

// ── LISTADO DE LEADS ─────────────────────────────────────────────────────────

const LEADS_POR_PAGINA = 50;

const norm = (s) => String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

function ListadoLeads({ leads, onAbrirFicha }) {
  // Sesión 32 · Entrega B: búsqueda, filtros, orden, filas visibles y scroll se recuerdan durante la sesión
  const [busqueda, setBusqueda] = useEstadoPersistente('leads.busqueda', '');
  const [filtroAnio, setFiltroAnio] = useEstadoPersistente('leads.filtroAnio', '');
  const [ordenacion, setOrdenacion] = useEstadoPersistente('leads.ordenacion', 'fecha_desc');
  const [visibles, setVisibles] = useEstadoPersistente('leads.visibles', LEADS_POR_PAGINA);
  usePosicionScroll('leads');

  const anioActual = new Date().getFullYear();
  const aniosDisponibles = [...new Set(leads.map(l => {
    const f = l.fecha_alta?.toDate ? l.fecha_alta.toDate() : null;
    return f ? f.getFullYear() : null;
  }).filter(Boolean))].sort((a, b) => b - a);

  // Reset paginación al cambiar filtros
  useAlCambiar(() => setVisibles(LEADS_POR_PAGINA), [busqueda, filtroAnio, ordenacion]);

  const leadsFiltrados = leads.filter(l => {
    const texto = norm(busqueda);
    const coincideTexto = !busqueda ||
      norm(`${l.nombre} ${l.apellidos}`).includes(texto) ||
      norm(l.empresa).includes(texto) ||
      norm(l.email).includes(texto) ||
      (l.movil || '').includes(texto) ||
      (l.telefono || '').includes(texto);
    const coincideAnio = !filtroAnio || (l.fecha_alta?.toDate && l.fecha_alta.toDate().getFullYear() === parseInt(filtroAnio));
    return coincideTexto && coincideAnio;
  }).sort((a, b) => {
    if (ordenacion === 'fecha_desc') return (b.fecha_alta?.seconds || 0) - (a.fecha_alta?.seconds || 0);
    if (ordenacion === 'fecha_asc') return (a.fecha_alta?.seconds || 0) - (b.fecha_alta?.seconds || 0);
    if (ordenacion === 'nombre_asc') return (a.nombre || '').localeCompare(b.nombre || '');
    return 0;
  });

  const kpiTotal = leadsFiltrados.length;
  const kpiEsteAnio = leadsFiltrados.filter(l => l.fecha_alta?.toDate && l.fecha_alta.toDate().getFullYear() === anioActual).length;
  const kpiConEmpresa = leadsFiltrados.filter(l => l.empresa).length;
  const hayFiltros = busqueda || filtroAnio;

  return (
    <>
      <div style={styles.gridKpis}>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Total leads{hayFiltros ? ' (filtrado)' : ''}</p>
          <p style={styles.kpiValor}>{kpiTotal}</p>
        </div>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Este año{hayFiltros ? ' (filtrado)' : ''}</p>
          <p style={styles.kpiValor}>{kpiEsteAnio}</p>
        </div>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Con empresa{hayFiltros ? ' (filtrado)' : ''}</p>
          <p style={styles.kpiValor}>{kpiConEmpresa}</p>
        </div>
        <div style={styles.kpiCard}>
          <p style={styles.kpiLabel}>Mostrando</p>
          <p style={{ ...styles.kpiValor, color: hayFiltros ? '#BA7517' : '#ffffff' }}>{leadsFiltrados.length}</p>
        </div>
      </div>

      <div style={styles.filtrosBar}>
        <input style={styles.buscador} placeholder="Buscar por nombre, empresa, teléfono, email..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <select style={styles.select} value={filtroAnio} onChange={e => setFiltroAnio(e.target.value)}>
          <option value="">Todos los años</option>
          {aniosDisponibles.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select style={styles.select} value={ordenacion} onChange={e => setOrdenacion(e.target.value)}>
          <option value="fecha_desc">Más recientes</option>
          <option value="fecha_asc">Más antiguos</option>
          <option value="nombre_asc">Nombre A-Z</option>
        </select>
        {hayFiltros && (
          <button style={styles.botonLimpiar} onClick={() => { setBusqueda(''); setFiltroAnio(''); }}>
            Limpiar filtros
          </button>
        )}
      </div>

      {leadsFiltrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#AFA9EC' }}>
          {hayFiltros ? 'No hay leads que coincidan con los filtros.' : 'Aún no hay leads registrados. Pulsa + Nuevo lead para empezar.'}
        </div>
      ) : (
        <div style={styles.tablaWrapper}>
          <table style={styles.tabla}>
            <thead>
              <tr>
                {['Nombre / Empresa','Contacto','Fuente','Fecha alta',''].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leadsFiltrados.slice(0, visibles).map(lead => (
                <tr key={lead.id} style={styles.tr} onClick={() => onAbrirFicha(lead)}>
                  <td style={styles.td}>
                    <span style={styles.nombreLead}>{lead.nombre} {lead.apellidos || ''}</span>
                    {lead.empresa && <span style={styles.subLead}>{lead.empresa}</span>}
                  </td>
                  <td style={styles.td}>
                    {lead.movil && <span style={styles.subLead}>{lead.movil}</span>}
                    {lead.email && <span style={styles.subLead}>{lead.email}</span>}
                  </td>
                  <td style={styles.td}>{lead.fuente || '—'}</td>
                  <td style={styles.td}>{formatearFechaCorta(lead.fecha_alta)}</td>
                  <td style={styles.td}>
                    <button style={styles.botonVer} onClick={e => { e.stopPropagation(); onAbrirFicha(lead); }}>Ver →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {leadsFiltrados.length > visibles && (
        <div style={{ textAlign: 'center', padding: '24px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <button
            style={{ ...styles.botonSecundario, padding: '10px 32px' }}
            onClick={() => setVisibles(v => v + LEADS_POR_PAGINA)}
          >
            Mostrar 50 más
          </button>
          <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>
            Mostrando {Math.min(visibles, leadsFiltrados.length)} de {leadsFiltrados.length} — usa el buscador para filtrar
          </span>
        </div>
      )}
    </>
  );
}

// ── LISTADO DE OPORTUNIDADES ─────────────────────────────────────────────────

function ListadoOportunidades({ oportunidades, onActualizar, leads, onAbrirFicha, onEditarOportunidad }) {
  const { sectores } = useListasConfig(['sectores']);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroSector, setFiltroSector] = useState('');
  const [busqueda, setBusqueda] = useState('');


  const filtradas = oportunidades.filter(o => {
    const coincideEstado = !filtroEstado || o.estado === filtroEstado;
    const coincideSector = !filtroSector || o.sector === filtroSector;
    const texto = busqueda.toLowerCase();
    const coincideTexto = !busqueda ||
      (o.leadNombre || '').toLowerCase().includes(texto) ||
      (o.leadEmpresa || '').toLowerCase().includes(texto) ||
      (o.ciudad || '').toLowerCase().includes(texto) ||
      (o.sector || '').toLowerCase().includes(texto);
    return coincideEstado && coincideSector && coincideTexto;
  }).sort((a, b) => (b.fecha_entrada?.seconds || 0) - (a.fecha_entrada?.seconds || 0));

  const total = oportunidades.length;
  const pendientes = oportunidades.filter(o => o.estado === 'pendiente').length;
  const presupuestados = oportunidades.filter(o => o.estado === 'presupuestado').length;
  const aceptados = oportunidades.filter(o => o.estado === 'aceptado').length;
  const perdidos = oportunidades.filter(o => o.estado === 'perdido').length;
  const descartados = oportunidades.filter(o => o.estado === 'descartado').length;
  const hayFiltros = filtroEstado || filtroSector || busqueda;

  const cambiarEstado = async (oportunidad, nuevoEstado) => {
    try {
      await updateDoc(doc(db, 'leads', oportunidad.leadId, 'posibles_proyectos', oportunidad.id), { estado: nuevoEstado });
      onActualizar();
    } catch (e) { console.error(e); }
  };



  return (
    <>
      <div style={styles.gridKpis}>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Total</p><p style={styles.kpiValor}>{total}</p></div>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Pendientes</p><p style={{ ...styles.kpiValor, color: '#534AB7' }}>{pendientes}</p></div>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Presupuestadas</p><p style={{ ...styles.kpiValor, color: '#BA7517' }}>{presupuestados}</p></div>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Aceptadas</p><p style={{ ...styles.kpiValor, color: '#1D9E75' }}>{aceptados}</p></div>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Perdidas</p><p style={{ ...styles.kpiValor, color: '#D85A30' }}>{perdidos}</p></div>
        <div style={styles.kpiCard}><p style={styles.kpiLabel}>Descartadas</p><p style={{ ...styles.kpiValor, color: '#6b7280' }}>{descartados}</p></div>
      </div>

      <div style={{ ...styles.gridKpis, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', marginBottom: '24px' }}>
        <KpiConversion label="Tasa de presupuestación" num={presupuestados + aceptados} den={total} descripcion="Oportunidades que llegaron a generar un presupuesto" />
        <KpiConversion label="Conversión sobre presupuestados" num={aceptados} den={presupuestados + aceptados} descripcion="Presupuestos aceptados sobre presupuestos enviados" />
        <KpiConversion label="Conversión global" num={aceptados} den={total} descripcion="Proyectos aceptados sobre total de oportunidades" />
      </div>

      <div style={styles.filtrosBar}>
        <input style={styles.buscador} placeholder="Buscar por lead, empresa, ciudad, sector..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <div style={styles.pills}>
          {ESTADOS_POSIBLE.map(e => (
            <button key={e.valor} style={{
              ...styles.pill,
              background: filtroEstado === e.valor ? e.color : 'transparent',
              color: filtroEstado === e.valor ? '#fff' : '#AFA9EC',
              borderColor: filtroEstado === e.valor ? e.color : 'rgba(83,74,183,0.3)',
            }} onClick={() => setFiltroEstado(filtroEstado === e.valor ? '' : e.valor)}>
              {e.etiqueta}
            </button>
          ))}
        </div>
        <select style={styles.select} value={filtroSector} onChange={e => setFiltroSector(e.target.value)}>
          <option value="">Todos los sectores</option>
          {sectores.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {hayFiltros && (
          <button style={styles.botonLimpiar} onClick={() => { setFiltroEstado(''); setFiltroSector(''); setBusqueda(''); }}>
            Limpiar filtros
          </button>
        )}
      </div>

      {filtradas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#AFA9EC' }}>
          {hayFiltros ? 'No hay oportunidades que coincidan con los filtros.' : 'Aún no hay oportunidades registradas.'}
        </div>
      ) : (
        <div style={styles.tablaWrapper}>
          <table style={styles.tabla}>
            <thead>
              <tr>
                {['Lead','Sector','Ubicación','Superficie','Presupuesto est.','Estado','Fecha',''].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtradas.map(op => (
                <tr key={op.id} style={{ ...styles.tr, cursor: 'pointer' }} onClick={() => onEditarOportunidad(op)}>
                  <td style={styles.td}>
                    <span style={styles.nombreLead}>{op.leadNombre}</span>
                    {op.leadEmpresa && <span style={styles.subLead}>{op.leadEmpresa}</span>}
                  </td>
                  <td style={styles.td}>{op.sector || '—'}</td>
                  <td style={styles.td}>{[op.ciudad, op.provincia, op.pais].filter(Boolean).join(', ') || '—'}</td>
                  <td style={styles.td}>{op.superficie ? `${fmtNum(op.superficie)} m²` : '—'}</td>
                  <td style={styles.td}>{formatearEuros(op.presupuesto_estimado)}</td>
                  <td style={styles.td} onClick={e => e.stopPropagation()}>
                    <select
                      style={{
                        background: estadoPosible(op.estado).color,
                        color: '#fff', border: 'none', borderRadius: '20px',
                        padding: '4px 10px', fontSize: '0.75rem', fontWeight: '500',
                        cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', outline: 'none',
                      }}
                      value={op.estado}
                      onChange={e => cambiarEstado(op, e.target.value)}
                    >
                      {ESTADOS_POSIBLE.map(e => (
                        <option key={e.valor} value={e.valor} style={{ background: '#1a1440', color: '#ffffff' }}>
                          {e.etiqueta}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={styles.td}>{formatearFechaCorta(op.fecha_entrada)}</td>
                  <td style={styles.td} onClick={e => e.stopPropagation()}>
                    <button style={styles.botonVer} onClick={() => {
                      const lead = leads.find(l => l.id === op.leadId);
                      if (lead) onAbrirFicha(lead);
                    }}>
                      Ver lead →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

// ── EDITAR OPORTUNIDAD ───────────────────────────────────────────────────────

function FormularioAltaLead({ onGuardar, onCancelar }) {
  // Sesión 34: borrador del alta (se conserva si sales a otro módulo sin guardar)
  const descartarBorrador = useDescartarBorrador();
  const { fuentes } = useListasConfig(['fuentes']);
  const FORM_INICIAL = {
    fecha_alta: new Date().toISOString().split('T')[0],
    nombre: '', apellidos: '', empresa: '',
    movil: '', telefono: '', email: '',
    direccion: '', localidad: '', provincia: '', pais: 'España',
    fuente: '', notas: '',
  };
  const [form, setForm, recuperado] = useBorrador('lead:nuevo', FORM_INICIAL);
  const [avisoBorrador, setAvisoBorrador] = useState(recuperado);
  const [guardando, setGuardando] = useState(false);
  const [errores, setErrores] = useState({});
  const [modal, setModal] = useState(false);
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const hayDatos = JSON.stringify(form) !== JSON.stringify(FORM_INICIAL);

  const validar = () => {
    const e = {};
    if (!form.nombre.trim()) e.nombre = 'El nombre es obligatorio';
    if (!form.fecha_alta) e.fecha_alta = 'La fecha es obligatoria';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email no válido';
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  const guardar = async () => {
    if (!validar()) return;
    setGuardando(true);
    try {
      const docRef = await addDoc(collection(db, 'leads'), {
        ...form, fecha_alta: new Date(form.fecha_alta), creado_en: serverTimestamp(),
      });
      const snap = await getDoc(docRef);
      descartarBorrador('lead:nuevo');
      onGuardar({ id: snap.id, ...snap.data() });
    } catch (e) { console.error(e); setGuardando(false); }
  };

  const intentarCancelar = () => {
    if (hayDatos) { setModal(true); } else { onCancelar(); }
  };

  return (
    <div style={{ ...styles.container, paddingBottom: '100px' }}>
      {modal && <ModalConfirmar mensaje="¿Quieres salir sin guardar este lead?" onConfirmar={() => { descartarBorrador('lead:nuevo'); onCancelar(); }} onCancelar={() => setModal(false)} />}
      <div style={styles.cabecera}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button style={styles.botonVolver} onClick={intentarCancelar}>← Leads</button>
          <h1 style={styles.titulo}>Nuevo lead</h1>
        </div>
      </div>
      {avisoBorrador && (
        <div style={{ padding: '16px 32px 0' }}>
          <AvisoBorrador onDescartar={() => { setForm(FORM_INICIAL); setAvisoBorrador(false); }} />
        </div>
      )}

      <div style={styles.formGrid}>
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Datos obligatorios</h2>
          <div style={styles.camposGrid}>
            <Campo label="Fecha de alta *" error={errores.fecha_alta}>
              <input type="date" style={estiloInput(errores.fecha_alta)} value={form.fecha_alta} onChange={e => set('fecha_alta', e.target.value)} />
            </Campo>
            <Campo label="Nombre *" error={errores.nombre}>
              <input style={estiloInput(errores.nombre)} value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre" />
            </Campo>
          </div>
        </div>

        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Datos de contacto</h2>
          <div style={styles.camposGrid}>
            <Campo label="Apellidos"><input style={estiloInput()} value={form.apellidos} onChange={e => set('apellidos', e.target.value)} placeholder="Apellidos" /></Campo>
            <Campo label="Empresa"><input style={estiloInput()} value={form.empresa} onChange={e => set('empresa', e.target.value)} placeholder="Nombre de empresa" /></Campo>
            <Campo label="Móvil"><input style={estiloInput()} value={form.movil} onChange={e => set('movil', e.target.value)} placeholder="+34 600 000 000" /></Campo>
            <Campo label="Teléfono fijo"><input style={estiloInput()} value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="+34 981 000 000" /></Campo>
            <Campo label="Email" error={errores.email}>
              <input style={estiloInput(errores.email)} value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@ejemplo.com" />
            </Campo>
          </div>
        </div>

        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Ubicación del contacto</h2>
          <div style={styles.camposGrid}>
            <Campo label="Dirección" fullWidth><input style={estiloInput()} value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Calle y número" /></Campo>
            <Campo label="Localidad"><input style={estiloInput()} value={form.localidad} onChange={e => set('localidad', e.target.value)} placeholder="Ciudad" /></Campo>
            <Campo label="Provincia"><input style={estiloInput()} value={form.provincia} onChange={e => set('provincia', e.target.value)} placeholder="Provincia" /></Campo>
            <Campo label="País"><input style={estiloInput()} value={form.pais} onChange={e => set('pais', e.target.value)} /></Campo>
          </div>
        </div>

        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Clasificación</h2>
          <div style={styles.camposGrid}>
            <Campo label="Fuente">
              <select style={estiloInput()} value={form.fuente} onChange={e => set('fuente', e.target.value)}>
                <option value="">Seleccionar fuente</option>
                {fuentes.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </Campo>
          </div>
        </div>

        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Notas</h2>
          <textarea style={{ ...estiloInput(), width: '100%', minHeight: '100px', resize: 'vertical' }} value={form.notas} onChange={e => set('notas', e.target.value)} placeholder="Notas iniciales sobre este lead..." />
        </div>
      </div>

      <div style={styles.barraGuardar}>
        <button style={styles.botonSecundario} onClick={intentarCancelar}>Cancelar</button>
        <button style={styles.botonPrimario} onClick={guardar} disabled={guardando}>
          {guardando ? 'Guardando...' : 'Guardar lead'}
        </button>
      </div>
    </div>
  );
}

// ── FICHA DE LEAD ────────────────────────────────────────────────────────────

function FichaLead({ lead, onVolver, esAdmin, onActualizar, onNuevaOportunidad, onEliminarLead, onNavegar }) {
  const { fuentes } = useListasConfig(['fuentes']);
  // Sesión 34: borrador — si quedaron cambios sin guardar de esta ficha, se reabre en edición con ellos
  const claveBorrador = `lead:${lead.id}`;
  const hayBorrador = useHayBorrador(claveBorrador);
  const [modoEdicion, setModoEdicion] = useState(hayBorrador);
  const [avisoBorrador, setAvisoBorrador] = useState(hayBorrador);
  const [datosLocales, setDatosLocales] = useState(lead);
  const [form, setForm, , reiniciarForm] = useBorrador(claveBorrador, { ...lead, fecha_alta: formatearFechaInput(lead.fecha_alta) });
  const [guardando, setGuardando] = useState(false);
  const [modal, setModal] = useState(false);
  const [posiblesProyectos, setPosiblesProyectos] = useState([]);
  const [mostrarFormPosible, setMostrarFormPosible] = useState(false);
  const [notas, setNotas] = useState([]);
  const [notaNueva, setNotaNueva] = useState('');
  const [guardandoNota, setGuardandoNota] = useState(false);
  const [modalEliminarLead, setModalEliminarLead] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  // Sesión 34: la oportunidad se abre en la ficha común (lectura + Editar); aquí solo se guarda cuál está abierta
  const [opAbierta, setOpAbierta] = useState(null);

  useEffect(() => { cargarPosiblesProyectos(); cargarNotas(); }, []);

  const cargarPosiblesProyectos = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'leads', lead.id, 'posibles_proyectos'), orderBy('fecha_entrada', 'desc')));
      setPosiblesProyectos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
  };

  const cargarNotas = async () => {
    try {
      const snap = await getDocs(query(collection(db, 'leads', lead.id, 'notas'), orderBy('fecha', 'desc')));
      const notasSnap = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (notasSnap.length > 0) { setNotas(notasSnap); }
      else if (lead.notas) { setNotas([{ texto: lead.notas, fecha: lead.creado_en }]); }
    } catch (e) {
      if (lead.notas) setNotas([{ texto: lead.notas, fecha: lead.creado_en }]);
    }
  };

  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const guardarEdicion = async () => {
    setGuardando(true);
    try {
      await updateDoc(doc(db, 'leads', lead.id), { ...form, fecha_alta: new Date(form.fecha_alta) });
      reiniciarForm(form);
      setAvisoBorrador(false);
      setDatosLocales({ ...datosLocales, ...form });
      setModoEdicion(false);
      onActualizar();
    } catch (e) { console.error(e); }
    setGuardando(false);
  };

  const intentarCancelar = () => setModal(true);

  const confirmarSalida = () => {
    setAvisoBorrador(false);
    reiniciarForm({ ...datosLocales, fecha_alta: formatearFechaInput(datosLocales.fecha_alta) });
    setModoEdicion(false);
    setModal(false);
  };

  const agregarNota = async () => {
    if (!notaNueva.trim()) return;
    setGuardandoNota(true);
    try {
      await addDoc(collection(db, 'leads', lead.id, 'notas'), { texto: notaNueva, fecha: serverTimestamp() });
      setNotaNueva('');
      cargarNotas();
    } catch (e) { console.error(e); }
    setGuardandoNota(false);
  };

  // Sesión 34: solicitud de eliminación (comercial) o su rechazo (admin). onActualizar relee el lead (1 lectura).
  const cambiarSolicitud = (solicitud) => {
    setDatosLocales(d => ({ ...d, solicitud_eliminacion: solicitud }));
    onActualizar();
  };

  const eliminarLead = async () => {
    setEliminando(true);
    try {
      // Sesión 34: borrado común (operaciones.js): notas, oportunidades y el lead
      await ejecutarEliminacion('lead', lead.id, datosLocales);
      onEliminarLead(lead.id);
    } catch (e) { console.error(e); setEliminando(false); }
  };


  if (opAbierta) {
    const nombreLead = `${datosLocales.nombre || ''} ${datosLocales.apellidos || ''}`.trim() || 'Lead';
    return (
      <FichaOportunidad
        key={opAbierta.id}
        oportunidad={{ ...opAbierta, leadId: lead.id, leadNombre: nombreLead, leadEmpresa: datosLocales.empresa }}
        esAdmin={esAdmin}
        textoVolver={nombreLead}
        mostrarLead={false}
        onVolver={() => setOpAbierta(null)}
        onActualizada={(op) => setPosiblesProyectos(prev => prev.map(pp => (pp.id === op.id ? { ...pp, ...op } : pp)))}
        onEliminada={(id) => { setPosiblesProyectos(prev => prev.filter(pp => pp.id !== id)); setOpAbierta(null); }}
        onNavegar={onNavegar}
      />
    );
  }

  return (
    <div style={{ ...styles.container, paddingBottom: modoEdicion ? '100px' : '32px' }}>
      {modal && <ModalConfirmar mensaje="¿Quieres salir sin guardar los cambios de este lead?" onConfirmar={confirmarSalida} onCancelar={() => setModal(false)} />}
      {modalEliminarLead && (
        <ModalConfirmar
          titulo="Eliminar lead"
          mensaje={`¿Seguro que quieres eliminar a ${lead.nombre} ${lead.apellidos || ''}? Se eliminarán también todas sus oportunidades y notas. Esta acción no se puede deshacer.`}
          onConfirmar={eliminarLead}
          onCancelar={() => setModalEliminarLead(false)}
          textoCancelar="Cancelar"
          textoConfirmar={eliminando ? 'Eliminando...' : 'Sí, eliminar'}
        />
      )}

      <div style={styles.cabecera}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <button style={styles.botonVolver} onClick={() => modoEdicion ? setModal(true) : onVolver()}>← Leads</button>
          <h1 style={styles.titulo}>{datosLocales.nombre} {datosLocales.apellidos || ''}</h1>
        </div>
        {!modoEdicion && (
          <div style={{ display: 'flex', gap: '10px' }}>
            {esAdmin
              ? <button style={styles.botonEliminar} onClick={() => setModalEliminarLead(true)}>Eliminar lead</button>
              : <BotonSolicitarEliminacion coleccion="leads" docId={lead.id} solicitud={datosLocales.solicitud_eliminacion} nombreRegistro={`${datosLocales.nombre || ''} ${datosLocales.apellidos || ''}`.trim()} onCambio={cambiarSolicitud} />}
            <button style={styles.botonSecundario} onClick={() => setModoEdicion(true)}>Editar</button>
          </div>
        )}
      </div>

      {!modoEdicion && (
        <AvisoSolicitudEliminacion coleccion="leads" docId={lead.id} solicitud={datosLocales.solicitud_eliminacion} esAdmin={esAdmin}
          onCambio={cambiarSolicitud} onEliminar={() => setModalEliminarLead(true)} />
      )}
      {modoEdicion && avisoBorrador && (
        <div style={{ padding: '16px 32px 0' }}>
          <AvisoBorrador onDescartar={confirmarSalida} />
        </div>
      )}
      <div style={styles.fichaGrid}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Datos de contacto</h2>
            {modoEdicion ? (
              <div style={styles.camposGrid}>
                <Campo label="Fecha de alta"><input type="date" style={estiloInput()} value={form.fecha_alta} onChange={e => set('fecha_alta', e.target.value)} /></Campo>
                <Campo label="Nombre"><input style={estiloInput()} value={form.nombre} onChange={e => set('nombre', e.target.value)} /></Campo>
                <Campo label="Apellidos"><input style={estiloInput()} value={form.apellidos || ''} onChange={e => set('apellidos', e.target.value)} /></Campo>
                <Campo label="Empresa"><input style={estiloInput()} value={form.empresa || ''} onChange={e => set('empresa', e.target.value)} /></Campo>
                <Campo label="Móvil"><input style={estiloInput()} value={form.movil || ''} onChange={e => set('movil', e.target.value)} /></Campo>
                <Campo label="Teléfono"><input style={estiloInput()} value={form.telefono || ''} onChange={e => set('telefono', e.target.value)} /></Campo>
                <Campo label="Email"><input style={estiloInput()} value={form.email || ''} onChange={e => set('email', e.target.value)} /></Campo>
              </div>
            ) : (
              <div style={styles.datosGrid}>
                <DatoFila label="Alta" valor={formatearFechaCorta(datosLocales.fecha_alta)} />
                {datosLocales.empresa && <DatoFila label="Empresa" valor={datosLocales.empresa} />}
                {datosLocales.movil && <DatoFila label="Móvil" valor={datosLocales.movil} accion={`tel:${datosLocales.movil}`} />}
                {datosLocales.telefono && <DatoFila label="Teléfono" valor={datosLocales.telefono} accion={`tel:${datosLocales.telefono}`} />}
                {datosLocales.email && <DatoFila label="Email" valor={datosLocales.email} accion={`mailto:${datosLocales.email}`} />}
                <DatoFila label="Fuente" valor={datosLocales.fuente || '—'} />
              </div>
            )}
          </div>

          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Ubicación del contacto</h2>
            {modoEdicion ? (
              <div style={styles.camposGrid}>
                <Campo label="Dirección" fullWidth><input style={estiloInput()} value={form.direccion || ''} onChange={e => set('direccion', e.target.value)} /></Campo>
                <Campo label="Localidad"><input style={estiloInput()} value={form.localidad || ''} onChange={e => set('localidad', e.target.value)} /></Campo>
                <Campo label="Provincia"><input style={estiloInput()} value={form.provincia || ''} onChange={e => set('provincia', e.target.value)} /></Campo>
                <Campo label="País"><input style={estiloInput()} value={form.pais || ''} onChange={e => set('pais', e.target.value)} /></Campo>
              </div>
            ) : (
              <div style={styles.datosGrid}>
                {datosLocales.direccion && <DatoFila label="Dirección" valor={datosLocales.direccion} />}
                <DatoFila label="Localidad" valor={[datosLocales.localidad, datosLocales.provincia].filter(Boolean).join(', ') || '—'} />
                <DatoFila label="País" valor={datosLocales.pais || '—'} />
              </div>
            )}
          </div>

          {modoEdicion && (
            <div style={styles.bloque}>
              <h2 style={styles.bloqueTitulo}>Clasificación</h2>
              <div style={styles.camposGrid}>
                <Campo label="Fuente">
                  <select style={estiloInput()} value={form.fuente || ''} onChange={e => set('fuente', e.target.value)}>
                    <option value="">Sin fuente</option>
                    {fuentes.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </Campo>
              </div>
            </div>
          )}

          <div style={styles.bloque}>
            <h2 style={styles.bloqueTitulo}>Notas</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
              {notas.length === 0 && <p style={{ color: '#AFA9EC', fontSize: '0.85rem' }}>Sin notas</p>}
              {notas.map((n, i) => (
                <div key={n.id || i} style={styles.notaItem}>
                  <p style={styles.notaTexto}>{n.texto}</p>
                  <p style={styles.notaFecha}>{formatearFechaCorta(n.fecha)}</p>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <textarea style={{ ...estiloInput(), flex: 1, resize: 'none', minHeight: '60px' }} placeholder="Añadir nota..." value={notaNueva} onChange={e => setNotaNueva(e.target.value)} />
              <button style={styles.botonPrimario} onClick={agregarNota} disabled={guardandoNota}>
                {guardandoNota ? '...' : 'Añadir'}
              </button>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={styles.bloque}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ ...styles.bloqueTitulo, marginBottom: 0 }}>Oportunidades</h2>
              <button style={styles.botonPrimario} onClick={() => setMostrarFormPosible(true)}>+ Añadir</button>
            </div>

            {mostrarFormPosible && (
              <FormularioPosibleProyecto
                leadId={lead.id}
                onGuardar={(nuevaOp) => {
                  setMostrarFormPosible(false);
                  cargarPosiblesProyectos();
                  if (onNuevaOportunidad) onNuevaOportunidad(nuevaOp);
                }}
                onCancelar={() => setMostrarFormPosible(false)}
              />
            )}

            {posiblesProyectos.length === 0 && !mostrarFormPosible && (
              <p style={{ color: '#AFA9EC', fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' }}>Sin oportunidades registradas</p>
            )}

            {posiblesProyectos.map(pp => (
              <TarjetaPosibleProyecto key={pp.id} pp={pp} onVer={() => setOpAbierta(pp)} />
            ))}
          </div>
        </div>
      </div>

      {modoEdicion && (
        <div style={styles.barraGuardar}>
          <button style={styles.botonSecundario} onClick={intentarCancelar}>Cancelar</button>
          <button style={styles.botonPrimario} onClick={guardarEdicion} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── FORMULARIO POSIBLE PROYECTO ──────────────────────────────────────────────

function FormularioPosibleProyecto({ leadId, onGuardar, onCancelar }) {
  const { sectores } = useListasConfig(['sectores']);
  const [form, setForm] = useState({
    fecha_entrada: new Date().toISOString().split('T')[0],
    estado: 'pendiente', sector: '',
    direccion: '', cp: '', ciudad: '', provincia: '', pais: 'España',
    superficie: '', presupuesto_estimado: '', observaciones: '',
  });
  const [guardando, setGuardando] = useState(false);
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const guardar = async () => {
    setGuardando(true);
    try {
      const docRef = await addDoc(collection(db, 'leads', leadId, 'posibles_proyectos'), {
        ...form,
        fecha_entrada: new Date(form.fecha_entrada),
        superficie: form.superficie ? parseFloat(form.superficie) : null,
        presupuesto_estimado: form.presupuesto_estimado ? parseFloat(form.presupuesto_estimado) : null,
        creado_en: serverTimestamp(),
      });
      const snap = await getDoc(docRef);
      onGuardar({ id: snap.id, leadId, ...snap.data() });
    } catch (e) { console.error(e); setGuardando(false); }
  };

  return (
    <div style={styles.formPosible}>
      <div style={styles.camposGrid}>
        <Campo label="Fecha de contacto"><input type="date" style={estiloInput()} value={form.fecha_entrada} onChange={e => set('fecha_entrada', e.target.value)} /></Campo>
        <Campo label="Estado inicial">
          <select style={estiloInput()} value={form.estado} onChange={e => set('estado', e.target.value)}>
            {OPCIONES_ESTADO_MANUAL.map(e => <option key={e.valor} value={e.valor}>{e.etiqueta}</option>)}
          </select>
        </Campo>
        <Campo label="Sector">
          <select style={estiloInput()} value={form.sector} onChange={e => set('sector', e.target.value)}>
            <option value="">Sin sector</option>
            {sectores.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Campo>
        <Campo label="Superficie estimada (m²)"><input type="number" style={estiloInput()} value={form.superficie} onChange={e => set('superficie', e.target.value)} placeholder="0" /></Campo>
        <Campo label="Presupuesto estimado (€)"><input type="number" style={estiloInput()} value={form.presupuesto_estimado} onChange={e => set('presupuesto_estimado', e.target.value)} placeholder="0" /></Campo>
        <Campo label="Dirección del proyecto" fullWidth><input style={estiloInput()} value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Calle y número" /></Campo>
        <Campo label="Código postal"><input style={estiloInput()} value={form.cp} onChange={e => set('cp', e.target.value)} placeholder="00000" /></Campo>
        <Campo label="Ciudad"><input style={estiloInput()} value={form.ciudad} onChange={e => set('ciudad', e.target.value)} placeholder="Ciudad" /></Campo>
        <Campo label="Provincia"><input style={estiloInput()} value={form.provincia} onChange={e => set('provincia', e.target.value)} placeholder="Provincia" /></Campo>
        <Campo label="País"><input style={estiloInput()} value={form.pais} onChange={e => set('pais', e.target.value)} /></Campo>
        <Campo label="Observaciones" fullWidth>
          <textarea style={{ ...estiloInput(), resize: 'vertical', minHeight: '80px' }} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} placeholder="Detalles del posible proyecto..." />
        </Campo>
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
        <button style={styles.botonSecundario} onClick={onCancelar}>Cancelar</button>
        <button style={styles.botonPrimario} onClick={guardar} disabled={guardando}>
          {guardando ? 'Guardando...' : 'Guardar oportunidad'}
        </button>
      </div>
    </div>
  );
}

// ── TARJETA POSIBLE PROYECTO ─────────────────────────────────────────────────

// Sesión 34: la tarjeta solo resume. Ver abre la ficha común de oportunidad, donde se edita y se elimina.
function TarjetaPosibleProyecto({ pp, onVer }) {
  return (
    <div style={{ ...styles.tarjetaPosible, cursor: 'pointer' }} onClick={onVer}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <EstadoBadge estado={pp.estado} />
          {pp.sector && <span style={{ background: 'rgba(83,74,183,0.2)', color: '#AFA9EC', fontSize: '0.72rem', padding: '2px 8px', borderRadius: '20px' }}>{pp.sector}</span>}
          <span style={{ color: '#AFA9EC', fontSize: '0.78rem' }}>{formatearFechaCorta(pp.fecha_entrada)}</span>
        </div>
        <button style={styles.botonEditar} onClick={e => { e.stopPropagation(); onVer(); }}>Ver</button>
      </div>
      {(pp.ciudad || pp.direccion) && <p style={styles.ppDato}>{[pp.direccion, pp.cp, pp.ciudad, pp.provincia, pp.pais].filter(Boolean).join(', ')}</p>}
      <div style={{ display: 'flex', gap: '16px', marginTop: '6px', flexWrap: 'wrap' }}>
        {pp.superficie && <span style={styles.ppDato}>{fmtNum(pp.superficie)} m²</span>}
        {pp.presupuesto_estimado && <span style={{ ...styles.ppDato, color: '#7B6FE8' }}>{formatearEuros(pp.presupuesto_estimado)}</span>}
      </div>
      {pp.observaciones && <p style={{ ...styles.ppDato, marginTop: '8px', fontStyle: 'italic' }}>{pp.observaciones}</p>}
    </div>
  );
}

// ── NUEVA OPORTUNIDAD DIRECTA ────────────────────────────────────────────────

function FormularioNuevaOportunidadDirecta({ leads, onGuardar, onCancelar }) {
  const { sectores } = useListasConfig(['sectores']);
  const [leadSeleccionado, setLeadSeleccionado] = useState('');
  const [busquedaLead, setBusquedaLead] = useState('');
  const [form, setForm] = useState({
    fecha_entrada: new Date().toISOString().split('T')[0],
    estado: 'pendiente', sector: '',
    direccion: '', cp: '', ciudad: '', provincia: '', pais: 'España',
    superficie: '', presupuesto_estimado: '', observaciones: '',
  });
  const [guardando, setGuardando] = useState(false);
  const [modal, setModal] = useState(false);
  const set = (campo, valor) => setForm(f => ({ ...f, [campo]: valor }));

  const leadsFiltrados = leads.filter(l => {
    const t = norm(busquedaLead);
    return !t || norm(`${l.nombre || ''} ${l.apellidos || ''}`).includes(t) || norm(l.empresa).includes(t);
  }).slice(0, 8);
  const elegirLead = (l) => { setLeadSeleccionado(l.id); setBusquedaLead(''); };
  const teclaLead = useTecladoLista(busquedaLead ? leadsFiltrados : [], elegirLead, () => setBusquedaLead(''));

  const leadElegido = leads.find(l => l.id === leadSeleccionado);
  const formInicialOp = {
    fecha_entrada: new Date().toISOString().split('T')[0],
    estado: 'pendiente', sector: '', superficie: '',
    presupuesto_estimado: '', direccion: '', cp: '',
    ciudad: '', provincia: '', pais: 'España', observaciones: '',
  };
  const hayDatos = leadSeleccionado || JSON.stringify(form) !== JSON.stringify(formInicialOp);

  const guardar = async () => {
    if (!leadSeleccionado) return;
    setGuardando(true);
    try {
      const docRef = await addDoc(collection(db, 'leads', leadSeleccionado, 'posibles_proyectos'), {
        ...form,
        fecha_entrada: new Date(form.fecha_entrada),
        superficie: form.superficie ? parseFloat(form.superficie) : null,
        presupuesto_estimado: form.presupuesto_estimado ? parseFloat(form.presupuesto_estimado) : null,
        creado_en: serverTimestamp(),
      });
      const snap = await getDoc(docRef);
      const leadData = leads.find(l => l.id === leadSeleccionado) || {};
      onGuardar({
        id: snap.id,
        leadId: leadSeleccionado,
        leadNombre: `${leadData.nombre || ''} ${leadData.apellidos || ''}`.trim(),
        leadEmpresa: leadData.empresa,
        ...snap.data(),
      });
    } catch (e) { console.error(e); setGuardando(false); }
  };

  const intentarCancelar = () => {
    if (hayDatos) { setModal(true); } else { onCancelar(); }
  };

  return (
    <div style={{ ...styles.container, paddingBottom: '100px' }}>
      {modal && <ModalConfirmar mensaje="¿Quieres salir sin guardar esta oportunidad?" onConfirmar={onCancelar} onCancelar={() => setModal(false)} />}
      <div style={styles.cabecera}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button style={styles.botonVolver} onClick={intentarCancelar}>← Leads</button>
          <h1 style={styles.titulo}>Nueva oportunidad</h1>
        </div>
      </div>

      <div style={styles.formGrid}>
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Lead asociado *</h2>
          {leadElegido ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#0e0b24', borderRadius: '8px', padding: '12px 16px', border: '1px solid #1D9E75' }}>
              <div>
                <span style={{ color: '#ffffff', fontWeight: '500' }}>{leadElegido.nombre} {leadElegido.apellidos || ''}</span>
                {leadElegido.empresa && <span style={{ color: '#AFA9EC', fontSize: '0.82rem', marginLeft: '8px' }}>{leadElegido.empresa}</span>}
              </div>
              <button style={styles.botonEditar} onClick={() => { setLeadSeleccionado(''); setBusquedaLead(''); }}>Cambiar</button>
            </div>
          ) : (
            <div>
              <input style={styles.buscador} placeholder="Buscar lead por nombre o empresa..." value={busquedaLead} onChange={e => setBusquedaLead(e.target.value)} onKeyDown={teclaLead.onKeyDown} />
              {busquedaLead && (
                <div style={{ background: '#0e0b24', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', marginTop: '4px', overflow: 'hidden' }}>
                  {leadsFiltrados.length === 0
                    ? <p style={{ color: '#AFA9EC', padding: '12px 16px', fontSize: '0.85rem' }}>No se encontraron leads</p>
                    : leadsFiltrados.map((l, i) => (
                      <div key={l.id} style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid rgba(83,74,183,0.1)', ...(i === teclaLead.activo ? ESTILO_ACTIVO : {}) }}
                        onMouseEnter={() => teclaLead.setActivo(i)} onClick={() => elegirLead(l)}>
                        <span style={{ color: '#ffffff', fontSize: '0.88rem' }}>{l.nombre} {l.apellidos || ''}</span>
                        {l.empresa && <span style={{ color: '#AFA9EC', fontSize: '0.78rem', marginLeft: '8px' }}>{l.empresa}</span>}
                      </div>
                    ))}
                </div>
              )}
              {!leadSeleccionado && <p style={{ color: '#BA7517', fontSize: '0.78rem', marginTop: '6px' }}>Debes seleccionar un lead para continuar</p>}
            </div>
          )}
        </div>

        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>Datos de la oportunidad</h2>
          <div style={styles.camposGrid}>
            <Campo label="Fecha de contacto"><input type="date" style={estiloInput()} value={form.fecha_entrada} onChange={e => set('fecha_entrada', e.target.value)} /></Campo>
            <Campo label="Estado inicial">
              <select style={estiloInput()} value={form.estado} onChange={e => set('estado', e.target.value)}>
                {OPCIONES_ESTADO_MANUAL.map(e => <option key={e.valor} value={e.valor}>{e.etiqueta}</option>)}
              </select>
            </Campo>
            <Campo label="Sector">
              <select style={estiloInput()} value={form.sector} onChange={e => set('sector', e.target.value)}>
                <option value="">Sin sector</option>
                {sectores.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Campo>
            <Campo label="Superficie estimada (m²)"><input type="number" style={estiloInput()} value={form.superficie} onChange={e => set('superficie', e.target.value)} placeholder="0" /></Campo>
            <Campo label="Presupuesto estimado (€)"><input type="number" style={estiloInput()} value={form.presupuesto_estimado} onChange={e => set('presupuesto_estimado', e.target.value)} placeholder="0" /></Campo>
            <Campo label="Dirección del proyecto" fullWidth><input style={estiloInput()} value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Calle y número" /></Campo>
            <Campo label="Código postal"><input style={estiloInput()} value={form.cp} onChange={e => set('cp', e.target.value)} placeholder="00000" /></Campo>
            <Campo label="Ciudad"><input style={estiloInput()} value={form.ciudad} onChange={e => set('ciudad', e.target.value)} placeholder="Ciudad" /></Campo>
            <Campo label="Provincia"><input style={estiloInput()} value={form.provincia} onChange={e => set('provincia', e.target.value)} placeholder="Provincia" /></Campo>
            <Campo label="País"><input style={estiloInput()} value={form.pais} onChange={e => set('pais', e.target.value)} /></Campo>
            <Campo label="Observaciones" fullWidth>
              <textarea style={{ ...estiloInput(), resize: 'vertical', minHeight: '80px' }} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} placeholder="Detalles del posible proyecto..." />
            </Campo>
          </div>
        </div>
      </div>

      <div style={styles.barraGuardar}>
        <button style={styles.botonSecundario} onClick={intentarCancelar}>Cancelar</button>
        <button style={{ ...styles.botonPrimario, opacity: leadSeleccionado ? 1 : 0.5 }} onClick={guardar} disabled={guardando || !leadSeleccionado}>
          {guardando ? 'Guardando...' : 'Guardar oportunidad'}
        </button>
      </div>
    </div>
  );
}

// ── ESTILOS ──────────────────────────────────────────────────────────────────

const styles = {
  container: { padding: '0', maxWidth: '100%' },
  cabecera: { position: 'sticky', top: 0, zIndex: 50, background: '#0e0b24', borderBottom: '1px solid rgba(83,74,183,0.2)', padding: '20px 32px', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' },
  titulo: { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300', letterSpacing: '0.02em' },
  pestanas: { display: 'flex', marginBottom: '0', borderBottom: '1px solid rgba(83,74,183,0.2)', padding: '0 32px', background: '#0e0b24' },
  pestana: { background: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: '#AFA9EC', fontSize: '0.9rem', padding: '10px 20px', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', marginBottom: '-1px' },
  pestanaActiva: { color: '#ffffff', borderBottom: '2px solid #534AB7' },
  botonPrimario: { background: '#534AB7', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', fontWeight: '500', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonSecundario: { background: 'transparent', color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.4)', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonVolver: { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.88rem', cursor: 'pointer', padding: '0', fontFamily: 'DM Sans, sans-serif' },
  botonEditar: { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'DM Sans, sans-serif' },
  botonLimpiar: { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonEliminar: { background: 'transparent', color: '#D85A30', border: '1px solid #D85A30', borderRadius: '8px', padding: '10px 18px', fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  botonEliminarPeq: { background: 'transparent', color: '#D85A30', border: 'none', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'DM Sans, sans-serif' },
  botonVer: { background: 'transparent', color: '#7B6FE8', border: 'none', fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  barraGuardar: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1440', borderTop: '1px solid rgba(83,74,183,0.3)', padding: '16px 32px', display: 'flex', justifyContent: 'flex-end', gap: '12px', zIndex: 100 },
  gridKpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' },
  kpiCard: { background: '#1a1440', borderRadius: '10px', padding: '16px 20px', border: '1px solid rgba(83,74,183,0.2)' },
  kpiLabel: { color: '#AFA9EC', fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' },
  kpiValor: { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300' },
  filtrosBar: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px', alignItems: 'center' },
  buscador: { background: '#1a1440', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '9px 14px', color: '#fff', fontSize: '0.88rem', outline: 'none', minWidth: '240px', flex: '1', fontFamily: 'DM Sans, sans-serif' },
  pills: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  pill: { border: '1px solid', borderRadius: '20px', padding: '6px 14px', fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', background: 'transparent' },
  select: { background: '#1a1440', border: '1px solid rgba(83,74,183,0.3)', borderRadius: '8px', padding: '9px 14px', color: '#AFA9EC', fontSize: '0.88rem', outline: 'none', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' },
  tablaWrapper: { overflowX: 'auto' },
  tabla: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#AFA9EC', fontSize: '0.75rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '10px 14px', textAlign: 'left', borderBottom: '1px solid rgba(83,74,183,0.2)' },
  tr: { borderBottom: '1px solid rgba(83,74,183,0.1)', cursor: 'pointer' },
  td: { padding: '12px 14px', color: '#ffffff', fontSize: '0.88rem', verticalAlign: 'middle' },
  nombreLead: { display: 'block', fontWeight: '500' },
  subLead: { display: 'block', color: '#AFA9EC', fontSize: '0.78rem' },
  formGrid: { display: 'flex', flexDirection: 'column', gap: '20px' },
  seccion: { background: '#1a1440', borderRadius: '12px', padding: '24px', border: '1px solid rgba(83,74,183,0.2)' },
  seccionTitulo: { color: '#ffffff', fontSize: '0.9rem', fontWeight: '500', marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid rgba(83,74,183,0.2)' },
  camposGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' },
  label: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.04em' },
  fichaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', padding: '24px 32px 32px' },
  bloque: { background: '#1a1440', borderRadius: '12px', padding: '24px', border: '1px solid rgba(83,74,183,0.2)' },
  bloqueTitulo: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' },
  datosGrid: { display: 'flex', flexDirection: 'column', gap: '10px' },
  datoFila: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  datoLabel: { color: '#AFA9EC', fontSize: '0.82rem', flexShrink: 0 },
  datoValor: { color: '#ffffff', fontSize: '0.88rem', textAlign: 'right' },
  notaItem: { background: '#0e0b24', borderRadius: '8px', padding: '12px 14px', border: '1px solid rgba(83,74,183,0.15)' },
  notaTexto: { color: '#ffffff', fontSize: '0.88rem', lineHeight: '1.5', marginBottom: '4px' },
  notaFecha: { color: '#AFA9EC', fontSize: '0.75rem' },
  tarjetaPosible: { background: '#0e0b24', borderRadius: '8px', padding: '14px 16px', border: '1px solid rgba(83,74,183,0.2)', marginBottom: '10px' },
  ppDato: { color: '#AFA9EC', fontSize: '0.82rem' },
  formPosible: { background: '#0e0b24', borderRadius: '10px', padding: '16px', border: '1px solid rgba(83,74,183,0.3)', marginBottom: '16px' },
};
